import React, { memo, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { Input } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { tablet } from 'services/ui';
import { Colors } from 'context';
import { Button, ButtonSizes } from 'atoms';

import type { IHomeworkMutationResult } from 'core';

/**
 * Client-side mirror of the backend GitHub-URL regex (spec 0012 §3.2).
 * Kept character-for-character in step with `homeworks/services/submission.py`
 * — a looser mirror lets the form "pass" a URL the server then rejects with
 * INVALID_GITHUB_URL (the ref segment excludes whitespace server-side).
 */
export const GITHUB_URL_RE =
  /^https:\/\/github\.com\/[A-Za-z\d](?:[A-Za-z\d-]{0,38})?\/[A-Za-z\d._-]{1,100}(\/tree\/[^/\s]+(\/[^\s]*)?)?\/?$/;

/**
 * Client-side cap mirroring the server's `HOMEWORK_MAX_ZIP_MB` (default 50).
 * The server value is env-tunable, so this is a UX pre-check only — the
 * authority is FILE_TOO_LARGE coming back from submitHomework.
 */
export const MAX_ZIP_BYTES = 50 * 1024 * 1024;

interface IFormValues {
  githubUrl: string;
  file: File | null;
}

export interface IHomeworkSubmitForm {
  submitLoading: boolean;
  /** rev.3: link and/or file — at least one, both allowed. */
  onSubmit: (source: { githubUrl?: string; file?: File }) => Promise<IHomeworkMutationResult>;
  /** Present only when replacing an existing submission. */
  onCancel?: () => void;
}

const formatMb = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/**
 * rev.3: GitHub link AND ZIP picker shown together (UX modeled on
 * molecules/PhotoUpload: hidden input + button). Either one or both may be
 * filled; at least one is required. Validation is Formik + Yup; the server
 * re-validates everything (the regex/caps here only mirror it for instant
 * feedback).
 */
export const HomeworkSubmitForm = memo(
  ({ submitLoading, onSubmit, onCancel }: IHomeworkSubmitForm) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const validationSchema = useMemo(
      () =>
        Yup.object().shape({
          githubUrl: Yup.string()
            .trim()
            // Format is checked only when a link is actually present (empty
            // passes — the field is optional as long as a file is attached).
            .test(
              'github-format',
              `${t('homework.submit.githubInvalid')}`,
              (value) => !value || GITHUB_URL_RE.test(value.trim()),
            )
            // At-least-one rule: require the link only while no file is picked.
            .when('file', {
              is: (file: File | null) => !file,
              then: Yup.string()
                .trim()
                .required(`${t('homework.submit.sourceRequired')}`),
            }),
          file: Yup.mixed()
            .test(
              'is-zip',
              `${t('homework.submit.zipInvalid')}`,
              (file) => !file || (file as File).name.toLowerCase().endsWith('.zip'),
            )
            .test(
              'max-size',
              `${t('homework.submit.zipTooLarge')}`,
              (file) => !file || (file as File).size <= MAX_ZIP_BYTES,
            ),
        }),
      [t],
    );

    return (
      <Formik<IFormValues>
        initialValues={{ githubUrl: '', file: null }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          const trimmedUrl = values.githubUrl.trim();
          await onSubmit({
            githubUrl: trimmedUrl || undefined,
            file: values.file ?? undefined,
          });
        }}>
        {({ values, errors, touched, setFieldValue, setFieldTouched, handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <FieldWrapper>
              <FieldLabel>{t('homework.submit.modeGithub')}</FieldLabel>
              <Input
                value={values.githubUrl}
                placeholder={`${t('homework.submit.githubPlaceholder')}`}
                onChange={(event) => setFieldValue('githubUrl', event.target.value)}
                onBlur={() => setFieldTouched('githubUrl')}
              />
              {touched.githubUrl && errors.githubUrl && <ErrorText>{errors.githubUrl}</ErrorText>}
            </FieldWrapper>

            <FieldWrapper>
              <FieldLabel>{t('homework.submit.modeZip')}</FieldLabel>
              <HiddenInput
                type="file"
                accept=".zip"
                ref={fileInputRef}
                onChange={(event) => {
                  const file = event.target.files?.[0] ?? null;
                  setFieldValue('file', file);
                  setFieldTouched('file');
                  // Same file re-pick must fire onChange again.
                  // eslint-disable-next-line no-param-reassign
                  event.target.value = '';
                }}
              />
              <ZipRow>
                <Button
                  type="button"
                  variant="outline"
                  size={ButtonSizes.sm}
                  colors={{ borderColor: Colors.gray }}
                  onClick={() => fileInputRef.current?.click()}>
                  {t('homework.submit.zipPick')}
                </Button>
                {values.file && (
                  <>
                    <FileName>
                      {values.file.name} ({formatMb(values.file.size)})
                    </FileName>
                    <Button
                      type="button"
                      variant="text"
                      size={ButtonSizes.sm}
                      onClick={() => setFieldValue('file', null)}>
                      {t('homework.submit.zipClear')}
                    </Button>
                  </>
                )}
              </ZipRow>
              <HintText>{t('homework.submit.zipHint')}</HintText>
              {touched.file && errors.file && <ErrorText>{errors.file}</ErrorText>}
            </FieldWrapper>

            <ButtonsWrapper>
              {onCancel && (
                <Button type="button" variant="text" onClick={onCancel} disabled={submitLoading}>
                  {t('homework.submit.cancel')}
                </Button>
              )}
              <Button type="submit" isLoading={submitLoading} disabled={submitLoading}>
                {t('homework.submit.submit')}
              </Button>
            </ButtonsWrapper>
          </Form>
        )}
      </Formik>
    );
  },
);

const Form = styled.form`
  margin-top: 24px;
`;

const FieldWrapper = styled.div`
  margin-bottom: 24px;
`;

const FieldLabel = styled.p`
  margin: 0 0 8px;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.medium })}
`;

const HiddenInput = styled.input`
  display: none;
`;

const ZipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const FileName = styled.span`
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
  word-break: break-all;
`;

const HintText = styled.p`
  margin: 8px 0 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const ErrorText = styled.p`
  margin: 8px 0 0;
  color: ${Colors.signal_red};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 8px;

  ${tablet} {
    flex-direction: row;
    gap: 16px;
  }
`;
